import type { ContentRequiredPageId, CtaRequiredPageId, SeoRequiredPageId } from '../page-registry';
import type {
  CollectionCard,
  CompanyContent,
  ContentBundle,
  CTAContent,
  GiftingFeature,
  GiftingOption,
  NavigationContent,
  PageContent,
  PageSeoContent,
  PromiseItem,
  TestimonialContent,
  TimelineItem,
  ValueStatement,
} from '../schema';

export {
  COLLECTION_FILTER_GROUPS,
  COLLECTIONS,
  COLLECTIONS_PAGE_CONTENT,
  type Collection,
  type CollectionFabric,
  type CollectionFilterGroup,
  type CollectionFilterOption,
  type CollectionsHeroContent,
  type CollectionsStory,
  type CollectionsValueProp,
  type CollectionZari,
} from '../en-IN/collections';

export const siteContent = {
  siteUrl: 'https://sreesilks.com',
  siteName: 'Sree Silks',
  defaultTitle: 'ஸ்ரீ சில்க்ஸ் | பாரம்பரிய பட்டு சேலைகள்',
  titleTemplate: '%s | Sree Silks',
  defaultDescription:
    'திருமண பரிசுகள், பட்டு சேலைகள், மற்றும் தமிழ்நாட்டு பாரம்பரியத்தை கொண்டாடும் தனிப்பயன் பட்டுத் தேர்வுகள்.',
  defaultImage: '/assets/home-hero-bg.jpg',
  locale: 'ta',
  brandColor: '#17214b',
  sameAs: ['https://instagram.com/sssreesilks', 'https://www.facebook.com/sssreesilks'],
  contact: {
    telephone: '+91 88704 35979',
    email: 'info@sreesilks.com',
  },
  address: {
    streetAddress: 'Palanivel Chetty St',
    addressLocality: 'Jalakandapuram',
    addressRegion: 'Tamil Nadu',
    postalCode: '636501',
    addressCountry: 'IN',
  },
  preloadAssets: [
    {
      href: '/assets/optimized/primary-logo-header.webp',
      as: 'image',
      type: 'image/webp',
      fetchPriority: 'high',
    },
  ],
} satisfies ContentBundle['site'];

export const primaryNavigation = [
  {
    id: 'home',
    name: 'முகப்பு',
    href: '/',
    description: 'ஸ்ரீ சில்க்ஸின் பாரம்பரியத்தை அனுபவிக்கவும்',
    icon: 'home',
  },
  {
    id: 'products',
    name: 'தயாரிப்புகள்',
    href: '/products',
    description: 'நேரடி பட்டியல் மற்றும் வழிகாட்டப்பட்ட தேர்வுகளை காணுங்கள்',
    icon: 'collections',
  },
  {
    id: 'collections',
    name: 'கலெக்ஷன்கள்',
    href: '/collections',
    description: 'சிறப்பு சேலைகள் மற்றும் விழா தேர்வுகள்',
    icon: 'collections',
  },
  {
    id: 'wedding-gifting',
    name: 'திருமண பரிசுகள்',
    href: '/wedding-gifting',
    description: 'ஒவ்வொரு சடங்கிற்கும் தேர்ந்தெடுத்த பரிசு ஆலோசனை',
    icon: 'wedding',
  },
  {
    id: 'journey',
    name: 'பயணம்',
    href: '/journey',
    description: 'ஐந்து தலைமுறைகளின் பட்டு கைவினை',
    icon: 'journey',
  },
  {
    id: 'about',
    name: 'எங்களை பற்றி',
    href: '/about',
    description: 'எங்கள் பண்பும் பணியும் பற்றி அறியுங்கள்',
    icon: 'about',
  },
  {
    id: 'contact',
    name: 'தொடர்பு',
    href: '/contact',
    description: 'எங்கள் ஸ்டைலிஸ்ட் மற்றும் பரிசு நிபுணர்களை அணுகுங்கள்',
    icon: 'contact',
  },
] satisfies NavigationContent['primary'];

export const navigationContent = {
  primary: primaryNavigation,
  footer: primaryNavigation,
  menu: primaryNavigation,
} satisfies NavigationContent;

export const companyContent = {
  contactDetails: [
    {
      id: 'phone',
      icon: 'phone',
      content: '+91 88704 35979',
      href: 'tel:+918870435979',
      label: '+91 88704 35979 எண்ணிற்கு அழைக்கவும்',
    },
    {
      id: 'email',
      icon: 'mail',
      content: 'info@sreesilks.com',
      href: 'mailto:info@sreesilks.com',
      label: 'info@sreesilks.com க்கு மின்னஞ்சல் அனுப்பவும்',
    },
    {
      id: 'address',
      icon: 'map-pin',
      content: 'பழனிவேல் செட்டி தெரு, ஜலகண்டபுரம், தமிழ்நாடு 636501',
    },
  ],
  socialLinks: [
    {
      id: 'instagram',
      name: 'Instagram',
      href: 'https://instagram.com/sssreesilks',
      icon: 'instagram',
      ariaLabel: 'எங்கள் Instagram பக்கத்தை பாருங்கள்',
    },
    {
      id: 'facebook',
      name: 'Facebook',
      href: 'https://www.facebook.com/sssreesilks',
      icon: 'facebook',
      ariaLabel: 'எங்கள் Facebook பக்கத்தை பாருங்கள்',
    },
    {
      id: 'whatsapp',
      name: 'Whatsapp',
      href: 'https://api.whatsapp.com/send?phone=918870435979&text=Heyy!',
      icon: 'whatsapp',
      ariaLabel: 'WhatsApp இல் எங்களுடன் பேசுங்கள்',
    },
  ],
} satisfies CompanyContent;

export const ctas = {
  home: {
    id: 'cta.home',
    legacyId: '6hMIfl4Gm573POuCFMnFga',
    page: 'home',
    title: 'ஒவ்வொரு நூலிலும் பாரம்பரியம்',
    subtitle: 'எங்கள் நிரந்தர கலெக்ஷன்களை பாருங்கள், அல்லது உங்கள் பட்டு பயணத்தை எங்களுடன் தொடங்குங்கள்.',
    ctaText: 'உங்களுக்காக ஒரு காலத்தால் அழியாத தேர்வை உருவாக்கலாம்.',
    ctaUrl: '/contact',
  },
  about: {
    id: 'cta.about',
    legacyId: '6X89FRlptH3pctIesA6jnv',
    page: 'about',
    title: 'எதிர்காலத்திற்காக நெய்யப்பட்ட பாரம்பரியம்',
    subtitle: 'நாங்கள் உருவாக்கும் ஒவ்வொரு சேலையும் பாரம்பரியத்தையும் நாளைய வாக்குறுதியையும் கொண்டாடுகிறது.',
    ctaText: 'பாரம்பரியத்தை அறியுங்கள். எங்கள் கலெக்ஷன்களை பாருங்கள்.',
    ctaUrl: '/collections',
  },
  collections: {
    id: 'cta.collections',
    legacyId: '1mHuoBC5XOCi0Hx6SVToGv',
    page: 'collections',
    title: 'ஒவ்வொரு தருணத்திற்கும் சரியான தேர்வு',
    subtitle: 'ஒவ்வொரு சேலையின் பின்னாலும் சொல்லப்பட வேண்டிய கதை உள்ளது. உங்களுக்காக உருவான அழகை கண்டறியுங்கள்.',
    ctaText: 'திருமண பரிசுகளை காணுங்கள்',
    ctaUrl: '/wedding-gifting',
  },
  contact: {
    id: 'cta.contact',
    legacyId: '3LviNQDHVpkgtbAtYUDoiU',
    page: 'contact',
    title: 'அழகான ஒன்றை சேர்ந்து உருவாக்குவோம்',
    subtitle: 'ஒவ்வொரு உரையாடலும் ஒரு புதிய கதையை தொடங்குகிறது. உங்கள் அடுத்த நினைவைக் நெய்ய எங்களை அணுகுங்கள்.',
    ctaText: 'உங்கள் பயணத்தை தொடங்குங்கள்',
    ctaUrl: '/wedding-gifting',
  },
  journey: {
    id: 'cta.journey',
    legacyId: '3tx4xo5Rk9OhibhFsnjuG8',
    page: 'journey',
    title: 'எங்கள் பயணத்தில் இணைந்திடுங்கள்',
    subtitle: 'எங்கள் பாரம்பரியத்துடன் இணைவதற்கு தயாரா? மேலும் அறிய அல்லது தொடங்க எங்களை தொடர்புகொள்ளுங்கள்.',
    ctaText: 'இன்றே எங்கள் கதையின் ஒரு பகுதியாவீர்.',
    ctaUrl: '/contact',
  },
  'wedding-gifting': {
    id: 'cta.wedding-gifting',
    legacyId: '7C1ARN9sCJoYeSqWaKPT8',
    page: 'wedding-gifting',
    title: 'கொண்டாடும் தருணங்கள், என்றும் நிலைக்கும் நினைவுகள்',
    subtitle: 'ஒவ்வொரு கொண்டாட்டத்தையும் பாரம்பரியமாக மாற்றுங்கள். வாழ்நாள் நினைவுகளுக்கான கலெக்ஷன்களை பாருங்கள்.',
    ctaText: 'எங்கள் கலெக்ஷன்களை பாருங்கள்',
    ctaUrl: '/collections',
  },
} satisfies Record<CtaRequiredPageId, CTAContent>;

export const defaultGlobalCta = {
  id: 'cta.default-global',
  title: 'ஒவ்வொரு நூலிலும் பாரம்பரியம்',
  subtitle: 'எங்கள் நிரந்தர கலெக்ஷன்களை பாருங்கள், அல்லது உங்கள் பட்டு பயணத்தை எங்களுடன் தொடங்குங்கள்.',
  ctaText: 'ஆலோசனையை பதிவு செய்யுங்கள்',
  ctaUrl: '/contact',
} satisfies CTAContent;

export const seoContent = {
  home: {
    title: 'திருமண பரிசுகள் மற்றும் பட்டு கலெக்ஷன்கள்',
    description:
      'பாரம்பரியம் மற்றும் நவீன நுட்பம் இணைந்த திருமண பரிசுகள், பட்டு சேலைகள், மற்றும் தனிப்பயன் பரிசு தீர்வுகளை கண்டறியுங்கள்.',
    keywords: 'பட்டு சேலைகள், திருமண பரிசுகள், பாரம்பரிய சேலைகள், தமிழ்நாடு பட்டு, மணமகள் சேலைகள்',
    canonicalPath: '/',
  },
  about: {
    title: 'எங்களை பற்றி - பட்டு கைவினை பாரம்பரியம்',
    description:
      'பட்டு சேலைகளில் ஸ்ரீ சில்க்ஸின் பயணம், பாரம்பரிய கைவினையை பாதுகாக்கும் எங்கள் பண்பு, மற்றும் ஐந்து தலைமுறைகளின் நம்பிக்கை பற்றி அறியுங்கள்.',
    canonicalPath: '/about',
    type: 'article',
  },
  collections: {
    title: 'கலெக்ஷன்கள்',
    description:
      'ஸ்ரீ சில்க்ஸ் கலெக்ஷன்களை நேரடி பட்டியல், தனி கலெக்ஷன் பக்கங்கள், மற்றும் விரைவான விசாரணை வழிகளுடன் பாருங்கள்.',
    canonicalPath: '/collections',
  },
  contact: {
    title: 'தொடர்பு கொள்ளுங்கள்',
    description: 'கலெக்ஷன்கள், திருமண பரிசுகள், அல்லது தனிப்பயன் ஆர்டர்கள் குறித்து ஸ்ரீ சில்க்ஸை தொடர்புகொள்ளுங்கள்.',
    canonicalPath: '/contact',
  },
  journey: {
    title: 'தலைமுறைகளைக் கடக்கும் எங்கள் பயணம்',
    description:
      'பட்டு கைவினையில் தலைமுறைகளின் சிறப்பையும், பாரம்பரிய தொடக்கத்திலிருந்து நவீன புதுமை வரை ஸ்ரீ சில்க்ஸின் வளர்ச்சியையும் காணுங்கள்.',
    canonicalPath: '/journey',
    type: 'article',
  },
  'wedding-gifting': {
    title: 'திருமண பரிசுகள்: அன்பும் பாரம்பரியமும்',
    description:
      'மனமார்ந்த பரிசளிப்பின் கலைக்கு அருகில் வாருங்கள். தமிழ்நாட்டு மரபுகளை மதிக்கும் சேலைகள், வேஷ்டிகள், மற்றும் தனிப்பயன் பரிசு அனுபவங்களை கண்டறியுங்கள்.',
    canonicalPath: '/wedding-gifting',
    type: 'product',
  },
} satisfies Record<SeoRequiredPageId, PageSeoContent>;

export const homeCollections = [
  {
    image: '/assets/optimized/home-collection-1.webp',
    alt: 'நுட்பமான பொன் எல்லையுடன் பட்டு சேலை',
  },
  {
    image: '/assets/optimized/home-collection-2.webp',
    alt: 'காஞ்சிபுரம் நெய்தல் நுட்பத்தின் நெருக்கமான தோற்றம்',
  },
  {
    image: '/assets/optimized/home-collection-3.webp',
    alt: 'மாறுபட்ட நிறங்களுடன் நவீன பட்டு வடிவமைப்பு',
  },
] satisfies readonly CollectionCard[];

export const giftingFeatures = [
  {
    title: 'தனிப்பயன் சேலை தொகுப்புகள்',
    image: '/assets/optimized/editorial/home-concierge-curation.webp',
    link: '/wedding-gifting#bundles',
    altText: 'அழகாக அடுக்கப்பட்ட பட்டு சேலை பரிசுத் தொகுப்பு',
  },
  {
    title: 'அழகிய பரிசுப் பேக்கிங்',
    image: '/assets/optimized/editorial/home-concierge-packaging.webp',
    link: '/wedding-gifting#packaging',
    altText: 'பிரீமியம் பரிசுப் பேக்கிங்கில் மடிக்கப்பட்ட பாரம்பரிய பட்டு சேலை',
  },
  {
    title: 'தனிப்பட்ட வாழ்த்து குறிப்புகள்',
    image: '/assets/optimized/editorial/home-concierge-blessing.webp',
    link: '/wedding-gifting#personalization',
    altText: 'சடங்கு பட்டுத் துணியுடன் கைஎழுத்து பரிசுக் குறிப்பு',
  },
] satisfies readonly GiftingFeature[];

export const giftingOptions = [
  {
    title: 'தனிப்பயன் சேலை தொகுப்புகள்',
    description:
      'ஒவ்வொரு விழாவிற்கும் மரபு தரமான சேலைகள், பொருந்தும் பிளவுஸ் துணிகள், மற்றும் நினைவுச் சிறப்புகளை சேர்த்து தேர்வு செய்கிறோம்.',
    image: '/assets/optimized/wedding/wedding-gifting-saree-hamper.webp',
    altText: 'மல்லிகை, நகை பெட்டிகள், மற்றும் தீபங்களுடன் காஞ்சிபுரம் சேலை ஹாம்பர்',
    badge: 'சிக்னேச்சர் தேர்வு',
    turnaround: 'முன்னேற்பாடு: 3-4 வாரங்கள்',
  },
  {
    title: 'பாரம்பரிய வேஷ்டி கலெக்ஷன்கள்',
    description: 'கோவில் மணி, சந்தன நினைவுகள், மற்றும் சடங்கு அலங்காரங்களுடன் வேஷ்டி மற்றும் அங்கவஸ்திர தொகுப்புகள்.',
    image: '/assets/optimized/wedding/wedding-gifting-dhoti-set.webp',
    altText: 'பித்தளை மணி, சந்தன பெட்டி, வெற்றிலை உடன் வெள்ளை வேஷ்டி மற்றும் அங்கவஸ்திரம்',
    badge: 'கோவில் சடங்குகள்',
    turnaround: 'முன்னேற்பாடு: 2-3 வாரங்கள்',
  },
  {
    title: 'தனிப்பட்ட நினைவுக் குறிப்புகள்',
    description:
      'எங்கள் கைஎழுத்து கலைஞர்களுடன் சேர்ந்து பருத்தி காகிதத்தில் ஆசீர்வாதங்களை எழுதி, மெழுகு முத்திரை மற்றும் வெல்வெட் ரிப்பனுடன் முடிக்கலாம்.',
    image: '/assets/optimized/wedding/wedding-gifting-calligraphy-note.webp',
    altText: 'மெழுகுவர்த்தி மற்றும் முத்திரையுடன் திருமண வாழ்த்தை எழுதும் கலைஞர்',
    badge: 'சிக்னேச்சர் எழுத்து',
    turnaround: 'முன்னேற்பாடு: 7-10 நாட்கள்',
  },
] satisfies readonly GiftingOption[];

export const values = [
  {
    title: 'தரமான கைவினை',
    description: 'ஒவ்வொரு துணியும் கலைப்பொருளாக அமைய பட்டு நெய்தலில் உயர்ந்த தரத்தை காக்கிறோம்.',
    icon: 'star',
  },
  {
    title: 'வாடிக்கையாளர் முதலில்',
    description: 'உங்கள் திருப்தியே எங்கள் முன்னுரிமை. தனிப்பட்ட சேவையும் நினைவில் நிற்கும் அனுபவமும் தர முயல்கிறோம்.',
    icon: 'users',
  },
  {
    title: 'பாரம்பரியம் மற்றும் புதுமை',
    description: 'மரபு நுட்பங்களை மதித்தபடியே, இன்றைய அழகியலுடன் பொருந்தும் நவீன வடிவங்களை உருவாக்குகிறோம்.',
    icon: 'heart',
  },
] satisfies readonly ValueStatement[];

export const promises = [
  {
    title: 'நிலைத்தன்மை',
    description: 'தலைமுறைகள் கடந்து பாதுகாக்கப்படும் நிலையான துணிகளும் நிரந்தர வடிவங்களும்.',
  },
  {
    title: 'நம்பகத்தன்மை',
    description: 'தலைமுறைகளின் நம்பிக்கை, அனுபவம், மற்றும் ஒரே தரம் ஒவ்வொரு வாடிக்கையாளருக்கும் நிம்மதி தருகிறது.',
  },
  {
    title: 'அழகு',
    description: 'ஒவ்வொரு சேலையும் நயம், செழுமை, மற்றும் பண்பாட்டு பெருமையின் தனித்த கதையை சொல்கிறது.',
  },
  {
    title: 'சிறப்பு',
    description: 'சிறந்த துணித் தேர்விலிருந்து நுட்பமான கைவினை வரை சமரசமில்லா தரநிலைகள்.',
  },
] satisfies readonly PromiseItem[];

export const timeline = [
  {
    year: '1950கள்',
    title: 'தொடக்கங்கள்',
    description:
      'இளம்பிள்ளை கைநெசவுகளில் தொடங்கி, நுட்பமான வடிவங்களும் செழுமையான மென்மையும் கொண்ட தூய பட்டு சேலைகளை உருவாக்கினோம்.',
  },
  {
    year: '1960கள்',
    title: 'நெய்தலில் புதுமை',
    description:
      'மைசூர் தறிகளை கொண்டு வந்து அரை தானியக்க நெய்தலை அறிமுகப்படுத்தி, "நார் பட்டு" போன்ற கையொப்ப வடிவங்களை உருவாக்கினோம்.',
  },
  {
    year: '1970கள்',
    title: 'விரிந்த பரப்பு',
    description: 'சேலத்தில் மகாவிஷ்ணு டெக்ஸ்டைல்ஸை தொடங்கி, பாரம்பரிய கலைத்திறனை நவீன சந்தை தேவைகளுடன் இணைத்தோம்.',
  },
  {
    year: '1990கள்',
    title: 'சந்தை போக்குகளுடன் வளர்ச்சி',
    description:
      'கைநெசவு மரபை காத்தபடி பாலியஸ்டர் கலவைகள், சுடிதார் துணிகள், மற்றும் பிளவுஸ் துணிகளையும் அறிமுகப்படுத்தினோம்.',
  },
  {
    year: '2000கள்',
    title: 'திட்டமிட்ட மாற்றம்',
    description:
      'உள் உற்பத்தியிலிருந்து நம்பகமான சப்ளையர்களின் தரமான சேலைகள் மற்றும் துணிகளை தேர்வு செய்யும் முறைக்கு மாறினோம்.',
  },
  {
    year: '2010கள்',
    title: 'புதிய பார்வை',
    description:
      'சில்லறை மையமான வணிக முறை, தனிப்பயன் பரிசுத் தீர்வுகள், மற்றும் நவீன விருப்பங்களுக்கான கலெக்ஷன்களுக்கு அடித்தளம் அமைந்தது.',
  },
  {
    year: '2019',
    title: 'ஸ்ரீ சில்க்ஸ் சில்லறை தொடக்கம்',
    description:
      'ஜலகண்டபுரத்தில் எங்கள் முதன்மை கடையை திறந்து, பாரம்பரிய சேலைத் தேர்வுகளையும் பிரீமியம் பரிசு அனுபவங்களையும் இணைத்தோம்.',
  },
  {
    year: 'இன்று',
    title: 'எதிர்காலத்திற்கான புதுமை',
    description:
      'பாரம்பரியத்தை கொண்டாடியபடி உலக வாடிக்கையாளர்களை அடைய ஆன்லைன் தளங்களுக்கு விரிவடையும் நம்பகமான எத்னிக் வேர் பெயர்.',
  },
] satisfies readonly TimelineItem[];

const pageContent = {
  home: {
    id: 'home',
    title: 'ஸ்ரீ சில்க்ஸ்',
    description: 'பாரம்பரிய பட்டு சேலைகள் மற்றும் திருமண பரிசு அனுபவங்கள்.',
    sections: [
      {
        id: 'hero',
        title: 'திருமண பரிசுகள் மற்றும் கலெக்ஷன்கள்',
        description: 'பாரம்பரிய திருமண பரிசுகள், பட்டு சேலைகள், மற்றும் தனிப்பயன் பரிசு தீர்வுகள்.',
      },
      {
        id: 'testimonials',
        title: 'எங்கள் வாடிக்கையாளர்கள் சொல்வது',
        description: 'எங்கள் அன்பான வாடிக்கையாளர்களின் அழகான அனுபவங்கள்.',
      },
    ],
  },
  about: {
    id: 'about',
    title: 'ஸ்ரீ சில்க்ஸ் பற்றி',
    description: 'பட்டு கைவினை, தரம், மற்றும் நவீன கொண்டாட்டத்தின் பாரம்பரியம்.',
    sections: [
      {
        id: 'legacy',
        title: 'பட்டு கைவினையின் பாரம்பரியம்',
        description:
          'ஐந்து தலைமுறைகளின் துணி அறிவு, இன்றைய குடும்பங்களுக்கு சேலைகளை தேர்வு செய்யவும் பகிரவும் எங்களை வழிநடத்துகிறது.',
      },
      {
        id: 'promise',
        title: 'SREE வாக்குறுதி',
        description: 'நிலைத்தன்மை, நம்பகத்தன்மை, அழகு, மற்றும் சிறப்பு ஒவ்வொரு அனுபவத்தையும் வடிவமைக்கின்றன.',
      },
    ],
  },
  contact: {
    id: 'contact',
    title: 'ஸ்ரீ சில்க்ஸை தொடர்புகொள்ளுங்கள்',
    description: 'கலெக்ஷன்கள், திருமண பரிசுகள், மற்றும் தனிப்பயன் ஆர்டர்களுக்கு எங்கள் நிபுணர்களை அணுகுங்கள்.',
    sections: [
      {
        id: 'inquiry',
        title: 'நீங்கள் தேடுவது என்ன என்பதை சொல்லுங்கள்; அடுத்ததை நாங்கள் கவனிக்கிறோம்.',
        description: 'தயாரிப்பு வழிகாட்டல், பரிசு ஆலோசனை, இன்வாய்ஸ் உதவி, மற்றும் கடை வருகை ஒருங்கிணைப்பு.',
      },
      {
        id: 'testimonials',
        title: 'எங்கள் வாடிக்கையாளர்கள் சொல்வது',
      },
    ],
  },
  journey: {
    id: 'journey',
    title: 'தலைமுறைகளைக் கடக்கும் எங்கள் பயணம்',
    description: 'பட்டு கைவினையின் தலைமுறை வளர்ச்சியும் ஸ்ரீ சில்க்ஸின் வரலாறும்.',
    sections: [
      {
        id: 'timeline',
        title: 'தலைமுறைகளைக் கடக்கும் எங்கள் பயணம்',
        description: 'இளம்பிள்ளை கைநெசவுகளிலிருந்து வாடிக்கையாளர் முதன்மை பட்டு இல்லமாக வளர்ந்த பயணம்.',
      },
    ],
  },
  'wedding-gifting': {
    id: 'wedding-gifting',
    title: 'திருமண பரிசுகள்',
    description: 'தமிழ் திருமண மரபுகளுக்கான சேலைகள், வேஷ்டிகள், மற்றும் தனிப்பயன் பரிசு அனுபவங்கள்.',
    sections: [
      {
        id: 'gifting-options',
        title: 'தேர்ந்தெடுத்த பரிசு அனுபவங்கள்',
        description: 'ஒவ்வொரு சடங்கிற்கும் சேலை தொகுப்புகள், வேஷ்டி கலெக்ஷன்கள், மற்றும் நினைவுக் குறிப்புகள்.',
      },
      {
        id: 'faq',
        title: 'உங்கள் கேள்விகளுக்கு பதில்',
        description: 'திருமண பரிசு கேள்விகள் தயாரானபின் இங்கே சேர்க்கப்படும்.',
      },
    ],
  },
} satisfies Record<ContentRequiredPageId, PageContent>;

export const pages = pageContent;

export const testimonials = [
  {
    id: 'testimonial.5CqD9y8j5XsJLrBtInNNvU',
    legacyId: '5CqD9y8j5XsJLrBtInNNvU',
    page: 'contact',
    title: 'விரைவான அன்பான தொடர்பு',
    quote:
      'ஸ்ரீ சில்க்ஸ் குழு எப்போதும் ஒரு செய்தி தூரத்தில் உள்ளது. தயாரிப்பு விவரங்கள் அல்லது அனுப்பும் நேரம் எதுவாக இருந்தாலும், தொடர்பை மிகவும் எளிதாக்குகிறார்கள்.',
    rating: 4,
    name: 'Neha K',
    location: { city: 'Madurai', state: 'TN', country: 'IN' },
  },
  {
    id: 'testimonial.4CByE7XV1G42DnivpR1oNZ',
    legacyId: '4CByE7XV1G42DnivpR1oNZ',
    page: 'contact',
    title: 'நம்பகமான சேவை',
    quote:
      'ஆன்லைனில் ஆர்டர் செய்ய ஆரம்பத்தில் தயக்கம் இருந்தது. ஆனால் ஸ்ரீ சில்க்ஸ் என் எதிர்பார்ப்பை மீறியது. கேள்விகளுக்கு உடனே பதில், தெளிவான படங்கள், பாதுகாப்பான டெலிவரி.',
    rating: 5,
    name: 'Sowmya N',
    location: { city: 'LA', state: 'CA', country: 'US' },
  },
  {
    id: 'testimonial.5XitmiMpLIyaEF3M0CQwTU',
    legacyId: '5XitmiMpLIyaEF3M0CQwTU',
    page: 'contact',
    title: 'ஒவ்வொரு உரையாடலிலும் தனிப்பட்ட அக்கறை',
    quote:
      'பேக்கிங்கிற்கான ஒரு சிறப்பு கோரிக்கை வைத்தேன். அவர்கள் அதை அழகாக தனிப்பயனாக்கினர். சேவையின் அக்கறை என் வாங்குதலை மிகச் சிறப்பாக மாற்றியது.',
    rating: 5,
    name: 'Arunima P',
    location: { city: 'NYC', state: 'NY', country: 'US' },
  },
  {
    id: 'testimonial.1MjPHv4O8eqHYdAmlOOixt',
    legacyId: '1MjPHv4O8eqHYdAmlOOixt',
    page: 'contact',
    title: 'இலகுவான ஆர்டர் அனுபவம்',
    quote:
      'ஒரு குறிப்பிட்ட சேலை வடிவத்தை பற்றி WhatsApp இல் கேட்டேன். குழு உடனே பதிலளித்து சரியான சேலையை தேர்வு செய்ய உதவியது.',
    rating: 5,
    name: 'Divya S',
    location: { city: 'Coimbatore', state: 'TN', country: 'IN' },
  },
  {
    id: 'testimonial.1w3RC3zvDmjssWvB1MThnW',
    legacyId: '1w3RC3zvDmjssWvB1MThnW',
    page: 'contact',
    title: 'சிறந்த வாடிக்கையாளர் ஆதரவு',
    quote:
      'திருமண பரிசு தொகுப்பை தனிப்பயனாக்க சில கேள்விகள் இருந்தன. அவர்கள் பொறுமையாக ஒவ்வொரு விவரத்தையும் விளக்கினர்.',
    rating: 5,
    name: 'Shanvika R',
    location: { city: 'Erode', state: 'TN', country: 'IN' },
  },
  {
    id: 'testimonial.z5n56s1KROZjnwxonzZKJ',
    legacyId: 'z5n56s1KROZjnwxonzZKJ',
    page: 'home',
    title: 'அக்கறையுள்ள பிராண்ட்',
    quote:
      'ஜலகண்டபுரம் கடைக்குள் சென்ற தருணத்திலிருந்து தரத்திற்கும் பாரம்பரியத்திற்கும் உள்ள அர்ப்பணிப்பை உணர்ந்தேன்.',
    rating: 5,
    name: 'Meera N',
    location: { city: 'Salem', state: 'TN', country: 'IN' },
  },
  {
    id: 'testimonial.55hpsHX7bcDA3lweWc19s6',
    legacyId: '55hpsHX7bcDA3lweWc19s6',
    page: 'home',
    title: 'பண்பாட்டு இணைப்பு',
    quote:
      'வெளிநாட்டில் வாழும் எனக்கு உண்மையான தரமான சேலைகளை கண்டுபிடிப்பது சிரமமாக இருந்தது. அவர்களின் ஆன்லைன் சேவை அதை எளிதாக்கியது.',
    rating: 5,
    name: 'Priya K',
    location: { city: 'Chicago', state: 'IL', country: 'USA' },
  },
  {
    id: 'testimonial.3mf9ekQfsk6RT3zsWuZA4C',
    legacyId: '3mf9ekQfsk6RT3zsWuZA4C',
    page: 'home',
    title: 'சிறந்த தனிப்பயனாக்கம்',
    quote:
      'என் மகளின் நிச்சயதார்த்தத்திற்காக சேலை தொகுப்பு ஆர்டர் செய்தேன். நிறத் தேர்வு முதல் பேக்கிங் வரை அனைத்தும் குறைபாடின்றி இருந்தது.',
    rating: 5,
    name: 'Revathi M',
    location: { city: 'Coimbatore', state: 'TN' },
  },
  {
    id: 'testimonial.1kqtmUR4hEz8ItxKcZBSFW',
    legacyId: '1kqtmUR4hEz8ItxKcZBSFW',
    page: 'home',
    title: 'ஒப்பற்ற அழகும் தரமும்',
    quote:
      'இவ்வளவு நுட்பமான விவரங்களுடன் சேலைகளை நான் பார்த்ததில்லை. ஒவ்வொரு துணியும் அக்கறையுடன் உருவான கலைப்பொருளாக உணரப்படுகிறது.',
    rating: 4,
    name: 'Lakshmi S',
    location: { city: 'Bangalore', state: 'KA' },
  },
  {
    id: 'testimonial.hZLx1PTFZjatyCUqmZTvB',
    legacyId: 'hZLx1PTFZjatyCUqmZTvB',
    page: 'home',
    title: 'திருமண பரிசில் சிறப்பு',
    quote:
      'என் சகோதரியின் திருமணத்தை ஸ்ரீ சில்க்ஸ் மிகவும் சிறப்பாக்கியது. தனிப்பயன் பரிசுத் தீர்வுகள் எங்கள் குடும்பத்தில் பெரிதும் பாராட்டப்பட்டன.',
    rating: 5,
    name: 'Aarthi R',
    location: { city: 'Chennai', state: 'TN' },
  },
  {
    id: 'testimonial.4wzD4uasPaY6dNNQ3d1uUb',
    legacyId: '4wzD4uasPaY6dNNQ3d1uUb',
    page: 'home',
    quote: 'அழகான வடிவங்கள்',
    rating: 5,
    name: 'Malli',
    location: { city: 'Salem', state: 'TN' },
  },
] satisfies readonly TestimonialContent[];

export const faqs = [];

export const taContent = {
  locale: 'ta',
  site: siteContent,
  navigation: navigationContent,
  company: companyContent,
  seo: seoContent,
  ctas,
  defaultGlobalCta,
  pages,
  testimonials,
  faqs,
} satisfies ContentBundle;
